// ============================================================
// posture.ts — 全域型別定義檔
// 所有與姿勢分析相關的介面、枚舉、型別別名集中於此
// ============================================================

// ── 姿勢問題類型枚舉 ──────────────────────────────────────
export enum PostureIssueType {
    HEAD_FORWARD    = 'HEAD_FORWARD',       // 頭部前傾（Forward Head Posture）
    SHOULDER_UNEVEN = 'SHOULDER_UNEVEN',    // 肩膀不水平
    SPINE_TILT      = 'SPINE_TILT',         // 脊椎側傾
    ROUNDED_BACK    = 'ROUNDED_BACK',       // 圓背/駝背
    PELVIC_TILT     = 'PELVIC_TILT',        // 骨盆前傾
    KNEE_LOCKED     = 'KNEE_LOCKED',        // 膝蓋過度鎖死
    WEIGHT_SHIFT    = 'WEIGHT_SHIFT',       // 重心偏移
}

// ── 問題嚴重程度 ───────────────────────────────────────────
export enum IssueSeverity {
    LOW    = 'LOW',     // 輕微（建議改善）
    MEDIUM = 'MEDIUM',  // 中等（需要注意）
    HIGH   = 'HIGH',    // 嚴重（立即矯正）
}

// ── 評級系統（S → D） ────────────────────────────────────
export type PostureGrade = 'S' | 'A' | 'B' | 'C' | 'D';

// ── 單一姿勢問題 ───────────────────────────────────────────
export interface PostureIssue {
    type: PostureIssueType;
    severity: IssueSeverity;
    message: string;            // 給員工看的中文說明
    exercise: string;           // 對應的矯正運動 ID
    offsetValue?: number;       // 偏差數值（如角度、百分比）
}

// ── 各部位分項評分 ─────────────────────────────────────────
export interface PostureScoreBreakdown {
    head: number;               // 頭部分數（0-100）
    shoulders: number;          // 肩膀分數（0-100）
    spine: number;              // 脊椎分數（0-100）
    hips: number;               // 髖骨分數（0-100）
}

// ── 完整姿勢分析結果 ──────────────────────────────────────
export interface PostureAnalysisResult {
    scores: PostureScoreBreakdown;
    totalScore: number;         // 加權總分（0-100）
    grade: PostureGrade;        // 評級
    issues: PostureIssue[];     // 偵測到的問題列表
    timestamp: number;          // Unix 時間戳
}

// ── 訓練 Session 快照（每秒一筆） ─────────────────────────
export interface SessionSnapshot {
    timestamp: number;
    score: number;
    grade: PostureGrade;
    issues: PostureIssueType[];
}

// ── 提交至後端的訓練 Session 資料 ─────────────────────────
export interface PostureSessionPayload {
    staffId: number;
    sessionData: SessionSnapshot[];
    averageScore: number;
    duration: number;           // 單位：秒
}

// ── 後端回傳的 AI 建議結果 ────────────────────────────────
export interface AIFeedbackResponse {
    sessionId: number;
    aiSuggestion: string;
    knowledgeCards: KnowledgeCard[];
    createdAt: string;
}

// ── 知識卡片（終身學習材料） ──────────────────────────────
export interface KnowledgeCard {
    issueType: PostureIssueType;
    cause: string;              // 成因解說
    risk: string;               // 長期風險說明
    correction: string[];       // 矯正步驟清單
    standingTip: string;        // 工作現場即用提示
    exerciseId: string;         // 對應運動 ID
}

// ── 課程模組 ──────────────────────────────────────────────
export interface LearningModule {
    id: string;
    title: string;
    titleJP: string;            // 日文標題（商場使用）
    description: string;
    duration: number;           // 分鐘
    topics: LearningTopic[];
    quizQuestions: QuizQuestion[];
}

// ── 課程主題 ──────────────────────────────────────────────
export interface LearningTopic {
    id: string;
    title: string;
    content: string;
    imageUrl?: string;
    videoUrl?: string;
}

// ── 測驗題目 ──────────────────────────────────────────────
export interface QuizQuestion {
    id: string;
    question: string;
    options: string[];
    correctIndex: number;
    explanation: string;
}

// ── 矯正運動 ──────────────────────────────────────────────
export interface Exercise {
    id: string;
    name: string;
    nameJp: string;
    targetIssue: PostureIssueType;
    duration: string;           // 如 "3 分鐘"
    sets: number;
    reps: number;
    instructions: string[];
    canDoAtWorkstation: boolean; // 可否在工作崗位上進行
}

// ── 員工資料 ──────────────────────────────────────────────
export interface Staff {
    id: number;
    name: string;
    nameJp?: string;
    department: string;
    floor: string;
    avatarUrl?: string;
}

// ── 員工進度統計 ──────────────────────────────────────────
export interface StaffProgress {
    staffId: number;
    totalSessions: number;
    averageScore: number;
    latestGrade: PostureGrade;
    scoreHistory: { date: string; score: number }[];
    completedModules: string[];
    mostCommonIssue: PostureIssueType | null;
    weeklyImprovement: number; // ????
}
